import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SiteNav from '../components/SiteNav';
import { useTema } from '../hooks/useTema';

// GLI ARTICOLI DELLE GUIDE (2026-09-06).
//
// I testi arrivano da guide/guida-webdav-mac.html e guida-webdav-windows.html,
// che erano due pagine a se' stanti con un proprio foglio di stile, un proprio
// interruttore IT/EN e le immagini incorporate come data URI. Qui dentro non
// entra niente di tutto questo:
//   - la lingua la decide il sito (i18n), non un interruttore locale;
//   - lo stile e' quello del sito (.guida-corpo in App.css, scritto sui token
//     del tema), non la tavolozza della guida;
//   - le immagini sono file veri in public/guide/, referenziati normalmente.
//
// Il corpo si CARICA a parte invece di stare nel bundle: sono ~40 kB di testo
// che serve a chi apre la guida e a nessun altro, e il bundle e' un file solo
// senza divisione in pezzi. Cosi' chi non apre mai la guida non la scarica, e
// per correggere un refuso basta ripubblicare il file, senza ricostruire.
//
// dangerouslySetInnerHTML: il contenuto e' nostro e statico, scritto da noi in
// public/guide/. Non c'e' nessun percorso per cui un utente possa scriverci
// dentro — se un domani questi frammenti diventassero modificabili da qualcuno
// che non sia chi pubblica il sito, questa riga va rifatta.
const SLUG_VALIDI = ['mac', 'windows'];

export default function GuidaPage({ slug, onLogin, onRegister, onNavigateHome, onNews, onTerms, onPrivacy, onApriGuida }) {
  const { t, i18n } = useTranslation(['guide', 'about', 'common']);
  const { tema, cambiaTema } = useTema();
  const [corpo, setCorpo]   = useState(null);
  const [errore, setErrore] = useState(false);

  const lingua = i18n.language?.startsWith('it') ? 'it' : 'en';
  const valido = SLUG_VALIDI.includes(slug);
  const altro  = slug === 'mac' ? 'windows' : 'mac';

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!valido) return undefined;
    let vivo = true;
    setCorpo(null); setErrore(false);
    fetch(`/guide/${slug}-${lingua}.html`, { cache: 'no-cache' })
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
      .then(html => { if (vivo) setCorpo(html); })
      .catch(() => { if (vivo) setErrore(true); });
    return () => { vivo = false; };
  }, [slug, lingua, valido]);

  function goHome(hash) { return onNavigateHome(hash); }
  function vaiA(id) { return goHome(id === 'hero' ? undefined : id); }

  return (
    <div className="cps-page">
      <SiteNav
        pagina="guide"
        onVaiA={vaiA}
        onAbout={() => goHome()}
        onNews={onNews}
        onLogin={onLogin}
        onRegister={onRegister}
        tema={tema}
        onCambiaTema={cambiaTema}
      />

      <article className="guida-pagina">
        <div className="guida-wrap">
          <button type="button" className="guida-torna" onClick={() => goHome('faq')}>
            ← {t('guide:torna_alle_domande')}
          </button>

          {!valido && <p className="guida-stato">{t('guide:non_trovata')}</p>}
          {valido && errore && (
            <p className="guida-stato">
              {t('guide:errore_caricamento')}{' '}
              <button type="button" className="guida-riprova" onClick={() => setErrore(false)}>
                {t('guide:riprova')}
              </button>
            </p>
          )}
          {valido && !errore && !corpo && <p className="guida-stato">{t('guide:caricamento')}</p>}

          {valido && corpo && (
            <div className="guida-corpo" dangerouslySetInnerHTML={{ __html: corpo }} />
          )}

          {/* L'altra guida in fondo: chi ha due computer, o chi e' capitato
              sulla guida sbagliata, non deve tornare indietro a cercarla. */}
          {valido && corpo && (
            <div className="guida-altra">
              <span>{t('guide:altro_sistema')}</span>
              <button type="button" onClick={() => onApriGuida(altro)}>
                {altro === 'mac' ? t('guide:link_mac') : t('guide:link_windows')} →
              </button>
            </div>
          )}
        </div>
      </article>

      <footer className="cps-footer">
        <div className="cps-footer-inner">
          <div className="cps-footer-brand">
            <span>♾️ LifetimeCloud.me</span>
            <p>{t('about:footer_tagline')}</p>
          </div>
          <div className="cps-footer-links">
            <button onClick={() => goHome('how-it-works')}>{t('about:nav_how_it_works')}</button>
            <button onClick={() => goHome('plans')}>{t('about:nav_plans')}</button>
            <button onClick={() => goHome('security')}>{t('about:nav_security')}</button>
            <button onClick={() => goHome('faq')}>{t('about:nav_faq')}</button>
            <button onClick={onLogin}>{t('about:nav_login')}</button>
            <button onClick={onRegister}>{t('about:nav_register')}</button>
            <button onClick={onTerms}>{t('common:footer_terms')}</button>
            <button onClick={onPrivacy}>{t('common:footer_privacy')}</button>
          </div>
        </div>
        <div className="cps-footer-bottom">
          <span>{t('about:footer_copyright')}</span>
          <span>🔒 {t('about:footer_security_badge')}</span>
        </div>
      </footer>
    </div>
  );
}
